import React, { ReactNode, useEffect, useState } from 'react';
import { SplitFactory } from '@splitsoftware/splitio-react';
import type { ISplitFactoryChildProps } from '@splitsoftware/splitio-react/types/types';
import { getTokenContent } from '../../utils/token';
import { parseSplitYaml } from '../../feature-flags/parseSplitYaml';

const DEFAULT_SPLIT_KEY = 'anonymous';

export interface AdminSplitProviderProps {
  children: ReactNode;
}

/**
 * Provides Split.io (Harness) feature flag context keyed by the authenticated user's UUID.
 * Use only for temporary page/feature gating (e.g. WIP or phased rollout). Permanent authorization
 * should be handled through C2FO User Roles or Features.
 * Use usePageFeature(flagName) to check if a page/component is enabled for the current user.
 * When treatment is 'on', the page is enabled; otherwise show "Coming soon" (e.g. via ComingSoonNavLink).
 * In localhost mode (no VITE_SPLIT_IO_KEY), loads apps/admin/ui/split.yaml and uses it as the features map.
 */
export const AdminSplitProvider: React.FC<AdminSplitProviderProps> = ({ children }) => {
  const userUuid = getTokenContent()?.payload?.user?.uuid ?? DEFAULT_SPLIT_KEY;
  const splitKey = userUuid;

  const authorizationKey =
    typeof import.meta.env.VITE_SPLIT_IO_KEY === 'string' && import.meta.env.VITE_SPLIT_IO_KEY.length > 0
      ? import.meta.env.VITE_SPLIT_IO_KEY
      : 'localhost';

  const isLocalhost = authorizationKey === 'localhost';
  const [localhostFeatures, setLocalhostFeatures] = useState<Record<string, string> | null>(isLocalhost ? null : {});

  useEffect(() => {
    if (!isLocalhost) return;

    let mounted = true;
    import('@admin-ui-root/split.yaml?raw')
      .then((m) => parseSplitYaml(m.default, userUuid))
      .then((features) => {
        if (mounted) setLocalhostFeatures(features);
      })
      .catch(() => {
        if (mounted) setLocalhostFeatures({});
      });
    return () => {
      mounted = false;
    };
  }, [isLocalhost, userUuid]);

  const features = isLocalhost ? localhostFeatures : undefined;
  const readyToInit = !isLocalhost || features !== null;

  const sdkConfig: SplitIO.IBrowserSettings = {
    core: {
      authorizationKey,
      key: splitKey,
    },
    ...(features !== undefined && features !== null && { features }),
    startup: {
      readyTimeout: 3,
      requestTimeoutBeforeReady: 3,
      retriesOnFailureBeforeReady: 3,
    },
    ...(isLocalhost && {
      scheduler: { offlineRefreshRate: 15 },
    }),
  };

  if (!readyToInit) {
    return <div data-testid="admin-split-loading">{children}</div>;
  }

  return (
    <SplitFactory config={sdkConfig} updateOnSdkUpdate={true}>
      {({ isReady }: ISplitFactoryChildProps) =>
        isReady ? (
          <div data-testid="admin-split-provider">{children}</div>
        ) : (
          <div data-testid="admin-split-loading">{children}</div>
        )
      }
    </SplitFactory>
  );
};
